import { ADMIN_SETTLEMENT_STATUS_MAP, DerivedInvoiceDisplayState, InvoiceLineItemTypeSchema, SettlementStatusSchema } from '@neon-one/auctions-types';
import type { OutstandingOffer } from '@neon-one/auctions-types';
import {
  InvoiceStatus,
  collectibleCents,
  designStatus,
  invoiceDisplayStatus,
  moneyCollected,
  needsWork,
  netPaidCents,
  paymentQualifier,
  type InvoiceStatusValue,
} from './payment-status';
import type { PaymentInvoice } from './types';

export interface BidderRollup {
  bidderUuid: string;
  bidderName: string;
  bidderEmail: string;
  /** Every invoice this bidder holds, most recent first. */
  invoices: PaymentInvoice[];
  /**
   * The invoice still owed on — the only one anybody can act on, and therefore
   * the one whose status the row reports. Null once every invoice is settled.
   */
  openInvoice: PaymentInvoice | null;
  /** Where a row click lands: the open invoice, else the most recent. */
  drawerInvoice: PaymentInvoice | null;
  /** Held lots across every invoice. Zero renders as an em dash, not "0". */
  itemCount: number;
  dueCents: number;
  paidCents: number;
  /** Settled refunds, separate from submitted refunds. */
  refundedCents: number;
  /** Accepted by the processor and not yet settled. Never folded into the above. */
  pendingRefundCents: number;
  status: InvoiceStatusValue;
  qualifier?: string;
  /** Rendered verbatim; null is the muted "None on file" reading. */
  paymentMethodLabel: string | null;
  /** A lot is out with this bidder awaiting an answer that would reopen their balance. */
  hasPendingOffer: boolean;
  /** Has somewhere for a charge to land: an open invoice with a stored method. */
  hasCardOnFile: boolean;
  /** Charging them now would actually take money. */
  chargeable: boolean;
}

const STATUS = SettlementStatusSchema.enum;
const LINE_KIND = InvoiceLineItemTypeSchema.enum;
const MAX_QUALIFIERS = 2;
const QUALIFIER_SEPARATOR = ' · ';

// §2.5 — Only unpaid balances and terminal/claimed charges remain due.
const isOpen = (inv: PaymentInvoice): boolean =>
  needsWork(inv) || (inv.status === STATUS.processing && collectibleCents(inv) > 0);
const hasMoneyArrived = (inv: PaymentInvoice): boolean => moneyCollected(inv.status);

// Newest first. `createdAt` is on the wire for exactly this (ADR-012 §7):
// append-or-mint makes a bidder's invoice set genuinely ordered for the first
// time, and "their most recent invoice" is the drawer's fallback entry point.
const byNewestFirst = (a: PaymentInvoice, b: PaymentInvoice): number =>
  (b.createdAt ?? '').localeCompare(a.createdAt ?? '');

// §2.5 — Use invoice readings, not gross totals, to distinguish Refunded from Void.
function resolveStatus(
  openInvoice: PaymentInvoice | null,
  invoices: readonly PaymentInvoice[],
): InvoiceStatusValue {
  if (openInvoice) return invoiceDisplayStatus(openInvoice);
  const statuses = invoices.map(invoiceDisplayStatus);
  if (statuses.includes(InvoiceStatus.RefundFailed)) return InvoiceStatus.RefundFailed;
  if (statuses.includes(InvoiceStatus.Unconfirmed)) return InvoiceStatus.Unconfirmed;
  const collected = invoices.filter(hasMoneyArrived).map(invoiceDisplayStatus);
  if (collected.length === 0) {
    return statuses.includes(InvoiceStatus.NothingDue) ? InvoiceStatus.NothingDue : InvoiceStatus.Void;
  }
  if (collected.every((status) => status === InvoiceStatus.Refunded)) return InvoiceStatus.Refunded;
  // A bidder who kept any money reads Paid: the row answers whether they need anything,
  // and the Amount paid column already says how much was refunded or is settling back.
  // The invoice itself still reads Partially refunded in the drawer and the reports.
  return collected.includes(InvoiceStatus.Clearing) ? InvoiceStatus.Clearing : InvoiceStatus.Paid;
}

const REFUND_SETTLING = ADMIN_SETTLEMENT_STATUS_MAP[DerivedInvoiceDisplayState.RefundSettling].label;
const PARTIAL_REFUND_SETTLING = 'Partial refund settling';

// Beside Paid, a refund settling is necessarily partial: the bidder kept money.
function statusAndQualifier(openInvoice: PaymentInvoice | null, invoices: readonly PaymentInvoice[]) {
  const status = resolveStatus(openInvoice, invoices);
  const qualifier = resolveQualifier(invoices);
  return {
    status,
    qualifier: status === InvoiceStatus.Paid && qualifier
      ? qualifier.split(QUALIFIER_SEPARATOR).map((part) => (part === REFUND_SETTLING ? PARTIAL_REFUND_SETTLING : part))
        .join(QUALIFIER_SEPARATOR)
      : qualifier,
  };
}

function resolveQualifier(invoices: readonly PaymentInvoice[]): string | undefined {
  const qualifiers = invoices.flatMap((invoice) => paymentQualifier(invoice)?.split(QUALIFIER_SEPARATOR) ?? []);
  return [...new Set(qualifiers)].slice(0, MAX_QUALIFIERS).join(QUALIFIER_SEPARATOR) || undefined;
}

// A lot still on the line is one this bidder is holding. A declined line is not
// held — that is exactly what reassigning and releasing do to it — so the two
// fixture bidders whose every lot went back read an em dash rather than a zero.
const heldLots = (inv: PaymentInvoice): number =>
  inv.lineItems.filter((li) => li.kind === LINE_KIND.auction_item && !li.declined).length;

// The method a charge would actually use — the STORED one, which is the column's
// own question ("Stored method"). The open invoice's first, falling back to the
// most recently seen so a settled bidder's row still names the card on file.
//
// Reads `storedMethodLabel`, never `paymentMethodLabel`: the latter names what
// PAID, and is null until a charge settles, so this column read "None on file"
// for every bidder who had a card but had not been collected yet.
function resolveMethodLabel(
  openInvoice: PaymentInvoice | null,
  newestFirst: PaymentInvoice[],
): string | null {
  if (openInvoice?.storedMethodLabel) return openInvoice.storedMethodLabel;
  return newestFirst.find((inv) => inv.storedMethodLabel)?.storedMethodLabel ?? null;
}

// Every bidder holding a pending offer — keyed by the bidder the offer was sent
// TO, which is a question the invoice set cannot answer: an offer is against an
// ITEM, and a bidder can be the target of one that appears on nobody's invoice
// but someone else's. Read the payload's offer-grained array rather than
// walking lines for the copy of the offer the wire hangs on each of that item's
// lines — that copy is a convenience, and one item legitimately carries several
// of them. /* §2.5 bidders table — status cell */
export function pendingOfferTargets(offers: readonly OutstandingOffer[]): Set<string> {
  return new Set(offers.map((offer) => offer.bidderUuid));
}

function groupInvoices(invoices: readonly PaymentInvoice[]): Map<string, PaymentInvoice[]> {
  const grouped = new Map<string, PaymentInvoice[]>();
  for (const invoice of invoices) {
    const bucket = grouped.get(invoice.bidderUuid);
    if (bucket) bucket.push(invoice);
    else grouped.set(invoice.bidderUuid, [invoice]);
  }
  return grouped;
}

function bidderMoney(invoices: readonly PaymentInvoice[]) {
  return {
    dueCents: invoices.filter(isOpen).reduce((sum, invoice) => sum + collectibleCents(invoice), 0),
    paidCents: invoices.filter(hasMoneyArrived).reduce((sum, invoice) => sum + netPaidCents(invoice), 0),
    refundedCents: invoices.reduce((sum, invoice) => sum + (invoice.refundSettledCents ?? invoice.refundedCents), 0),
    pendingRefundCents: invoices.reduce(
      (sum, invoice) => sum + (invoice.refundPendingCents ?? invoice.pendingRefundCents ?? 0), 0,
    ),
  };
}

function bidderRow(bidderUuid: string, held: readonly PaymentInvoice[], offerTargets: ReadonlySet<string>): BidderRollup {
  const invoices = [...held].sort(byNewestFirst);
  const openInvoice = invoices.find(isOpen) ?? null;
  const money = bidderMoney(invoices);
  const hasCardOnFile = openInvoice !== null && openInvoice.storedMethodLabel !== null;
  return {
    bidderUuid, bidderName: invoices[0].bidderName, bidderEmail: invoices[0].bidderEmail ?? '',
    invoices, openInvoice, drawerInvoice: openInvoice ?? invoices[0] ?? null,
    itemCount: invoices.reduce((sum, invoice) => sum + heldLots(invoice), 0),
    ...money,
    ...statusAndQualifier(openInvoice, invoices),
    paymentMethodLabel: resolveMethodLabel(openInvoice, invoices),
    hasPendingOffer: offerTargets.has(bidderUuid), hasCardOnFile,
    chargeable: openInvoice !== null && hasCardOnFile && money.dueCents > 0
      && designStatus(openInvoice.status) === InvoiceStatus.AwaitingPayment,
  };
}

// §2.5 — Bidder figures cover every invoice, ordered due-descending then name A→Z.
export function rollUpByBidder(
  invoices: readonly PaymentInvoice[],
  outstandingOffers: readonly OutstandingOffer[],
): BidderRollup[] {
  const offerTargets = pendingOfferTargets(outstandingOffers);
  return [...groupInvoices(invoices)].map(([uuid, held]) => bidderRow(uuid, held, offerTargets))
    .sort((a, b) => b.dueCents - a.dueCents || a.bidderName.localeCompare(b.bidderName));
}
